import React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";

import { Button, ButtonGroup, Classes, ContextMenuTarget, FormGroup, H6, InputGroup, Menu, MenuItem, Popover, Tag, TextArea } from "@blueprintjs/core";
import * as dictionaryActions from "core/actions/dictionaryActions";
import initialState from "core/reducers/initialState";
import DictionaryUtil, { IDictionaryResult } from "core/util/dictionary/DictionaryUtil";
import TokenSession from "core/util/session/TokenSession";
import { ILabelDefinition } from "./ILabel";

/**
 * Connected edit form for a particular dictionary key.
 * Pass list of instance overrides to allow use specific overriding.
 *
 * @author chris
 */

// tslint:disable:max-classes-per-file


type OwnProps = ILabelDefinition & { readOnly?: boolean; };

interface IStateProps {
	entry: null | IDictionaryResult; // current most relevant value
}

interface IDispatchProps {
	actions: typeof dictionaryActions;
}

function mapStateToProps(state: typeof initialState = initialState, ownProps: OwnProps): IStateProps {

	return {
		entry: DictionaryUtil.resolveValue(
			state.dictionary,
			{ value: ownProps.v, type: ownProps.type },
			ownProps.k,
			state.user.language,
			ownProps.instances
		)
	};
}

function mapDispatchToProps(dispatch: Dispatch<typeof initialState>): IDispatchProps {

	return {
		actions: bindActionCreators(dictionaryActions, dispatch)
	};
}

type ResultProps = IStateProps & IDispatchProps & OwnProps;

interface IState {
	value: string;
	changed?: boolean;
	type?: string;
}

const labelInfoProps = {
	className: Classes.TEXT_MUTED + " " + Classes.TEXT_SMALL,
	style: { marginBottom: 5 }
};

class DictionaryEntryForm extends React.Component<ResultProps, IState> {

	public constructor(props: ResultProps) {
		super(props);

		this.state = {
			changed: false,
			value: (props.entry.value || "")
		};
	}

	public render() {

		const { allowMarkdown, argNames } = this.props;

		const isMarkdown = (this.state.type || this.props.entry.type || this.props.type) === 'MARKDOWN';

		return (
			<div key={this.props.k}>
				<FormGroup>
					<p {...labelInfoProps}>{`Key: ${this.props.k}`}</p>
					<p {...labelInfoProps}>{`Language: ${this.props.entry.language}`}</p>
					{
						this.props.entry.instance && (
							<p {...labelInfoProps}>{`Instance: ${this.props.entry.instance}`}</p>
						)
					}
				</FormGroup>
				{
					allowMarkdown && (
						<FormGroup label="Type">
							<ButtonGroup>
								<Button
									active={!isMarkdown}
									small={true}
									text="Plain Text"
									onClick={this.onChangeType('PLAIN')}
								/>
								<Button
									active={isMarkdown}
									small={true}
									text="Markdown"
									onClick={this.onChangeType('MARKDOWN')}
								/>
							</ButtonGroup>
						</FormGroup>
					)
				}
				{
					argNames && argNames.length > 0 && (
						<FormGroup label="Available Template Variables">
							{argNames.map((n) => (
								<Tag key={n} interactive={true} style={{margin: '0 5 5 0'}}>
									{'${' + n + '}'}
								</Tag>
							))}
						</FormGroup>
					)
				}
				<FormGroup label="Value">
					{
						isMarkdown ? (
							<TextArea
								onChange={this.onChange}
								value={this.state.value}
							/>
						) : (
							<InputGroup
								onChange={this.onChange}
								value={this.state.value}
							/>
						)
					}
				</FormGroup>
				<ButtonGroup>
					<Popover
						content={
							<Menu>
								<MenuItem key="global" text="Globally for key" onClick={this.onSave()} shouldDismissPopover={false} />
								{
									this.props.instances && this.props.instances.map((inst) => {

										return (
											<MenuItem
												key={inst}
												text={`Override: ${inst}`}
												shouldDismissPopover={false}
												onClick={this.onSave(inst)}
											/>
										);
									})
								}
							</Menu>
						}
					>
						<Button text="Save" small={true} disabled={!this.state.changed} />
					</Popover>
					<Button text={this.state.changed ? "Reset" : "Delete Override"} small={true} onClick={this.onDelete} />
				</ButtonGroup>
			</div>
		);
	}

	private onChange = (e: any) => {
		this.setState({
			value: e.target.value,
			changed: true
		});
	}

	private onChangeType = (type: string) => () => this.setState({type, changed: true});

	private onSave = (instance?: string) => () => {
		const res = {...this.props.entry};
		if (instance) {
			res.instance = instance;
		}
		if (this.state.type) {
			res.type = this.state.type;
		}
		res.value = this.state.value;

		this.setState({changed: false}, () => {
			this.props.actions.dictionarySaveEntry(res);
		});
	}

	private onDelete = () => {
		if (this.state.changed) {
			this.setState({
				value: this.props.entry.value,
				type: this.props.entry.type,
				changed: false
			});
		} else {
			this.props.actions.dictionaryDeleteEntry({
				key: this.props.entry.key,
				instance: this.props.entry.instance || null,
				language: this.props.entry.language
			});
		}
	}
}

@ContextMenuTarget
class FormPopover extends React.Component<ResultProps> {

	public render() {

		return (
			<span>
				{this.props.children}
			</span>
		);
	}

	public renderContextMenu() {

		return (
			<div className={Classes.DARK}>
				<Menu style={{padding: "15px", minWidth: "300px"}}>
					<H6>Edit Label</H6>
					<DictionaryEntryForm {...this.props} />
				</Menu>
			</div>
		);
	}
}

/**
 * direct use in editors
 */
export const DictionaryEntryFormPanel = connect(mapStateToProps, mapDispatchToProps)(DictionaryEntryForm);

/**
 * context menu popover for inline editing
 */
export const DictionaryEntryFormPopover = connect(mapStateToProps, mapDispatchToProps)(FormPopover);



// WEBPACK FOOTER //
// ./src/core/components/label/DictionaryEntryForm.tsx